import { useRef } from 'react';
import { cn } from '@/lib/format.ts';
import type { Attachment } from '@/lib/uploads.ts';
import { Button } from './ui.tsx';

/**
 * A file picker standing in for drag-and-drop, because dragging is not
 * something a keyboard or a screen reader can do. Same upload path either
 * way — this just supplies the `FileList` a drop event would have.
 */
export function AttachButton({
  onFiles,
  disabled,
}: {
  onFiles: (files: FileList) => void;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        className="hidden"
        onChange={(event) => {
          if (event.target.files?.length) onFiles(event.target.files);
          event.target.value = '';
        }}
      />
      <Button
        type="button"
        size="sm"
        tone="quiet"
        disabled={disabled}
        onClick={() => input.current?.click()}
      >
        Attach
      </Button>
    </>
  );
}

/**
 * What is pasted or dropped in, waiting to go out with the message.
 *
 * Nothing here is text yet — a finished upload is a pill, not a markdown
 * link sitting in the box for someone to trip over while they finish
 * writing. Each one can be pulled back out with the ✕ before Send turns it
 * into part of the message.
 */
export function AttachmentPills({
  items,
  onRemove,
}: {
  items: Attachment[];
  onRemove: (id: number) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      {items.map((item) => (
        <span
          key={item.id}
          className={cn(
            'inline-flex max-w-full items-center gap-1.5 rounded-full border py-1 pr-1 pl-2.5 text-[11.5px]',
            item.state === 'error' ? 'border-warnx/30 text-warnx' : 'border-line text-mute',
          )}
        >
          <span className="max-w-56 truncate">
            {item.state === 'uploading'
              ? `Uploading ${item.name}…`
              : item.state === 'error'
                ? `${item.name} — ${item.error}`
                : item.name}
          </span>
          <button
            type="button"
            onClick={() => onRemove(item.id)}
            aria-label={`Remove ${item.name}`}
            title="Remove"
            className="rg-focus grid size-4 shrink-0 place-items-center rounded-full text-faint hover:bg-btn hover:text-hi2"
          >
            ✕
          </button>
        </span>
      ))}
    </div>
  );
}
